import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, AsyncMultiSearchSelect, type AsyncSearchPageResult } from '@vipso/shared';
import {
  useLazyListCustomersQuery,
  useLazyListVendorsQuery,
  useLazyListProductsQuery,
} from '../../api/tenantApi';
import { useModuleLabel, useModuleTerminology } from '../../hooks/useModuleLabel';

export interface ProcurementEntityFiltersProps {
  customerIds: string[];
  vendorIds: string[];
  productIds: string[];
  customerLabels: Record<string, string>;
  vendorLabels: Record<string, string>;
  productLabels: Record<string, string>;
  onCustomerChange: (ids: string[], labels: Record<string, string>) => void;
  onVendorChange: (ids: string[], labels: Record<string, string>) => void;
  onProductChange: (ids: string[], labels: Record<string, string>) => void;
}

export function ProcurementEntityFilters({
  customerIds,
  vendorIds,
  productIds,
  customerLabels,
  vendorLabels,
  productLabels,
  onCustomerChange,
  onVendorChange,
  onProductChange,
}: ProcurementEntityFiltersProps) {
  const { t } = useTranslation();
  const customersLabel = useModuleLabel('customer', 'customers.title');
  const vendorsLabel = useModuleLabel('vendor', 'vendors.title');
  const productsLabel = useModuleLabel('product', 'products.title');
  const customerTerms = useModuleTerminology('customer');
  const vendorTerms = useModuleTerminology('vendor');
  const productTerms = useModuleTerminology('product');
  const [fetchCustomers] = useLazyListCustomersQuery();
  const [fetchVendors] = useLazyListVendorsQuery();
  const [fetchProducts] = useLazyListProductsQuery();

  const fetchCustomerPage = useCallback(
    async (args: { search: string; page: number; pageLimit: number }): Promise<AsyncSearchPageResult> => {
      const result = await fetchCustomers({
        search: args.search || undefined,
        page: args.page,
        pageLimit: args.pageLimit,
      }).unwrap();
      const rows = result.listCustomers?.rows ?? [];
      return {
        rows: rows.map((c) => ({ value: c.id, label: c.name })),
        total: result.listCustomers?.total ?? 0,
      };
    },
    [fetchCustomers],
  );

  const fetchVendorPage = useCallback(
    async (args: { search: string; page: number; pageLimit: number }): Promise<AsyncSearchPageResult> => {
      const result = await fetchVendors({
        search: args.search || undefined,
        page: args.page,
        pageLimit: args.pageLimit,
      }).unwrap();
      const rows = result.listVendors?.rows ?? [];
      return {
        rows: rows.map((v) => ({ value: v.id, label: v.name })),
        total: result.listVendors?.total ?? 0,
      };
    },
    [fetchVendors],
  );

  const fetchProductPage = useCallback(
    async (args: { search: string; page: number; pageLimit: number }): Promise<AsyncSearchPageResult> => {
      const result = await fetchProducts({
        search: args.search || undefined,
        page: args.page,
        pageLimit: args.pageLimit,
      }).unwrap();
      const rows = result.listProducts?.rows ?? [];
      return {
        rows: rows.map((p) => ({ value: p.id, label: p.name })),
        total: result.listProducts?.total ?? 0,
      };
    },
    [fetchProducts],
  );

  return (
    <Stack gap="sm">
      <AsyncMultiSearchSelect
        label={customersLabel}
        placeholder={t('terminology.search', { entity: customerTerms.plural })}
        values={customerIds}
        valueLabels={customerLabels}
        onChange={onCustomerChange}
        fetchPage={fetchCustomerPage}
      />
      <AsyncMultiSearchSelect
        label={vendorsLabel}
        placeholder={t('terminology.search', { entity: vendorTerms.plural })}
        values={vendorIds}
        valueLabels={vendorLabels}
        onChange={onVendorChange}
        fetchPage={fetchVendorPage}
      />
      <AsyncMultiSearchSelect
        label={productsLabel}
        placeholder={t('terminology.search', { entity: productTerms.plural })}
        values={productIds}
        valueLabels={productLabels}
        onChange={onProductChange}
        fetchPage={fetchProductPage}
      />
    </Stack>
  );
}
